<template>
  <Dialog :visible="visible" modal header="Adicionar Conta" @update:visible="$emit('update:visible', $event)" :draggable="false" class="w-full max-w-md">
    <div class="p-4 flex flex-col gap-4">
      <div class="flex flex-col">
        <label for="name" class="font-semibold mb-2">Nome</label>
        <InputText id="name" v-model="name" class="w-full" />
      </div>
      <div class="flex flex-col">
        <label for="platform-country" class="font-semibold mb-2">Plataforma</label>
        <PlatformCountrySelect
          id="platform-country"
          v-model="platformCountryCode"
          placeholder="Selecionar plataforma"
          class="w-full"
        />
      </div>
      <div class="flex flex-col">
        <label for="cnpj" class="font-semibold mb-2">CNPJ</label>
        <InputText id="cnpj" v-model="cnpj" :class="{ 'p-invalid': !isCnpjValid && cnpj.length > 0 }" class="w-full"/>
        <small v-if="!isCnpjValid && cnpj.length > 0" class="p-error text-red-500 mt-1">O CNPJ deve ter 14 caracteres.</small>
      </div>
      <div class="flex flex-col">
        <label for="regulators" class="font-semibold mb-2">Reguladores</label>
        <RegulatorPicker
          id="regulators"
          v-model="selectedRegulators"
          multiple
          :regulators="platformRegulators"
          :disabled="platformCountryCode === null"
          :placeholder="platformCountryCode === null ? 'Selecione a plataforma primeiro' : 'Selecionar regulador'"
        />
      </div>
    </div>
    <template #footer>
      <Button label="Cancelar" @click="$emit('update:visible', false)" class="p-button-text">
        <template #icon>
          <span class="material-icons">close</span>
        </template>
      </Button>
      <Button label="Salvar" @click="create" :disabled="!isFormValid">
        <template #icon>
          <span class="material-icons">check</span>
        </template>
      </Button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import RegulatorPicker from '@/components/common/RegulatorPicker.vue';
import PlatformCountrySelect from '@/components/common/PlatformCountrySelect.vue';
import type { PlatformCountry } from '@/platformCountry';
import { isCnpjPattern } from '@/utils/validators';
import { normalizeCnpj } from '@/utils/formatters';
import regulatorsApi from '@/api/modules/regulators';
import type { Regulator } from '@/types/regulators';

const props = defineProps<{ visible: boolean }>();
const emit = defineEmits(['update:visible', 'create']);

const name = ref('');
const cnpj = ref('');
// No default: the admin must pick the platform, and only then its regulators.
const platformCountryCode = ref<PlatformCountry | null>(null);
const selectedRegulators = ref<string[]>([]);
const platformRegulators = ref<Regulator[]>([]);

// A platform's regulators are those of its country; switching platform resets the choice.
watch(platformCountryCode, async (platformCountry) => {
  selectedRegulators.value = [];
  platformRegulators.value = [];
  if (platformCountry === null) return;
  try {
    platformRegulators.value = await regulatorsApi.list(platformCountry);
  } catch {
    platformRegulators.value = [];
  }
});

const isCnpjValid = computed(() => {
  return isCnpjPattern(cnpj.value, 'full');
});

const isFormValid = computed(() => {
  return name.value.trim() !== '' && isCnpjValid.value && platformCountryCode.value !== null;
});

// Reset the form each time the dialog opens.
watch(
  () => props.visible,
  (open) => {
    if (open) {
      name.value = '';
      cnpj.value = '';
      platformCountryCode.value = null;
      selectedRegulators.value = [];
    }
  },
);

const create = () => {
  if (!isFormValid.value || platformCountryCode.value === null) return;
  const cleanCnpj = normalizeCnpj(cnpj.value);
  emit('create', {
    name: name.value,
    cnpj: cleanCnpj,
    platform_country_code: platformCountryCode.value,
    regulators: [...selectedRegulators.value],
  });
  emit('update:visible', false);
};
</script>
